export const RENTAL_REQUIREMENTS = [
  "Valid government-issued ID",
  "Proof of income or employment",
  "Barangay clearance (when requested)",
  "Completed rental application form",
];

export const APARTMENT_POLICIES = [
  "No illegal activities inside the apartment unit or common areas.",
  "Observe quiet hours from 10:00 PM to 6:00 AM.",
  "Pets are subject to apartment policy and prior approval.",
  "Subleasing is not allowed without written approval.",
  "Keep hallways, stairs, and shared spaces clean and clear.",
  "Dispose of rubbish in designated bins and follow collection schedules.",
  "Smoking is not allowed inside units or enclosed common areas.",
  "Report leaks, damage, and maintenance concerns promptly.",
  "Visitors must respect quiet hours and community rules.",
  "Request approval before drilling, repainting, or altering the unit.",
];

export const LEASE_DURATIONS = [
  "6 months",
  "12 months",
  "18 months",
  "24 months",
];

export const APPLICATION_STATUSES = [
  "Pending",
  "Visited",
  "Documents Required",
  "Documents Submitted",
  "Documents Approved",
  "Assigned",
  "Approved",
  "Reserved",
  "Rejected",
  "Cancelled",
];

const ACCOUNTS_KEY = "rental_portal_accounts";
const APPLICATIONS_KEY = "rental_portal_applications";
const INQUIRIES_KEY = "rental_portal_inquiries";

const normalizeAmenities = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean).map(String);
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

const normalizeImages = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean).map(String);
  }
  if (typeof value === "string" && value.trim()) {
    return [value.trim()];
  }
  return [];
};

export const normalizeApartmentUnit = (raw, index = 0) => {
  const occupied =
    raw?.is_occupied === true || raw?.is_occupied === 1 || raw?.is_occupied === "1";
  const statusText = raw?.availability_status
    ? String(raw.availability_status)
    : occupied
    ? "Occupied"
    : "Available";

  const imageCandidates = [
    ...(normalizeImages(raw?.images)),
    ...(normalizeImages(raw?.photos)),
    ...(normalizeImages(raw?.photo_urls)),
    ...(normalizeImages(raw?.image_urls)),
    ...(normalizeImages(raw?.image)),
  ];

  const details = raw?.listing_details || {};
  return {
    details,
    id: raw?.id || `unit-${index}`,
    apartment_name: raw?.property_name || raw?.apartment_name || raw?.name || `Apartment Unit ${index + 1}`,
    location: raw?.location || "Location not specified",
    monthly_rent: Number(raw?.rent_price || raw?.monthly_rent || 0),
    availability_status: statusText,
    description:
      raw?.description ||
      "Contact The Vergara Rentals for full apartment details, lease terms, and move-in options.",
    amenities: normalizeAmenities(details.amenities || raw?.amenities || raw?.features),
    rental_requirements: normalizeAmenities(details.rental_requirements || raw?.rental_requirements || raw?.requirements),
    apartment_policies: normalizeAmenities(details.apartment_policies || raw?.apartment_policies || raw?.rules || raw?.policies),
    preferred_move_in: details.available_from || raw?.preferred_move_in || "",
    lease_duration: details.lease_duration || raw?.lease_duration || "",
    latitude: raw?.latitude ?? raw?.lat ?? null,
    longitude: raw?.longitude ?? raw?.lng ?? raw?.lon ?? null,
    images: [...new Set(imageCandidates)],
    raw,
  };
};

export const parseApartmentUnits = (payload) => {
  const candidates = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.results)
    ? payload.results
    : [];

  return candidates.map((item, index) => normalizeApartmentUnit(item, index));
};

export const money = (value) =>
  `PHP ${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const readLocalJson = (key, fallback = []) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || "null");
    return Array.isArray(parsed) ? parsed : fallback;
  } catch (error) {
    console.error(error);
    return fallback;
  }
};

export const writeLocalJson = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value));
};

export const createLocalAccount = ({ fullName, email, phone, password }) => {
  const accounts = readLocalJson(ACCOUNTS_KEY, []);
  const normalizedEmail = String(email || "").trim().toLowerCase();
  if (!normalizedEmail) {
    return { ok: false, message: "Email address is required." };
  }

  const exists = accounts.some((account) => account.email === normalizedEmail);
  if (exists) {
    return { ok: false, message: "Email already exists. Please log in instead." };
  }

  const next = [
    ...accounts,
    {
      id: `local-account-${Date.now()}`,
      name: fullName,
      email: normalizedEmail,
      phone,
      password,
      role: "tenant",
      created_at: new Date().toISOString(),
    },
  ];
  writeLocalJson(ACCOUNTS_KEY, next);
  return { ok: true };
};

export const loginWithLocalAccount = ({ email, password }) => {
  const accounts = readLocalJson(ACCOUNTS_KEY, []);
  const normalizedEmail = String(email || "").trim().toLowerCase();
  const match = accounts.find(
    (account) => account.email === normalizedEmail && account.password === password
  );
  if (!match) {
    return null;
  }
  return {
    user: {
      id: match.id,
      name: match.name,
      email: match.email,
      role: match.role || "tenant",
      phone: match.phone || "",
    },
    token: `local-token-${Date.now()}`,
  };
};

export const saveLocalRentalApplication = (payload) => {
  const applications = readLocalJson(APPLICATIONS_KEY, []);
  const next = [
    ...applications,
    {
      ...payload,
      id: `local-application-${Date.now()}`,
      application_status: "Pending",
      submitted_at: new Date().toISOString(),
      source: "local",
    },
  ];
  writeLocalJson(APPLICATIONS_KEY, next);
  return next[next.length - 1];
};

export const getLocalRentalApplications = (email) => {
  const applications = readLocalJson(APPLICATIONS_KEY, []);
  if (!email) {
    return applications;
  }
  return applications.filter(
    (item) => String(item.email || "").toLowerCase() === String(email).toLowerCase()
  );
};

export const saveLocalInquiry = (payload) => {
  const inquiries = readLocalJson(INQUIRIES_KEY, []);
  const next = [
    ...inquiries,
    {
      ...payload,
      id: `local-inquiry-${Date.now()}`,
      submitted_at: new Date().toISOString(),
    },
  ];
  writeLocalJson(INQUIRIES_KEY, next);
};
